import React from 'react';
import { Typography, Paper, useTheme, Grid, Box } from '@mui/material';
import MermaidDiagram from './process-flow/MermaidDiagram';

import { stageDetails } from '../data/processFlowData'; // Ensure this file exists and is correctly named
import { useProcessStage } from '@/hooks/useProcessStage';
import StageStepper from './process-flow/StageStepper';
import StageDetails from './process-flow/StageDetails';
import ProcessHeader from './process-flow/ProcessHeader';
import { ProcessStep } from '@/types/processFlowTypes';

interface ProcessFlowProps {
  currentStage?: string;
  completedSteps?: string[];
  onStageClick?: (node: ProcessStep) => void;
  isVisible?: boolean;
}

const ProcessFlow: React.FC<ProcessFlowProps> = ({
  currentStage: externalStage = '',
  completedSteps = [],
  onStageClick: externalStageClick = () => {},
  isVisible = true
}) => {
  const theme = useTheme();
  const { 
    currentStage, 
    selectedStep,
    handleStageClick 
  } = useProcessStage(externalStage || '', (stage) => externalStageClick(stage, ''));

  return (
    <Paper 
      elevation={2}
      sx={{ 
        p: '24px 20px 60px 20px',
        backgroundColor: '#ffffff',
        borderRadius: 4,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          transform: 'translateY(-1px)',
        }
      }}
    >      
      <ProcessHeader 
        stageDetails={stageDetails}
        currentStage={currentStage}
      />
      
      <Box sx={{ 
        width: '90%',
        mx: 'auto', // Center horizontally with auto margins
        display: 'flex',
        justifyContent: 'center', // Center the content within the Box
        pb: 2,
        '& .MuiStepper-root': {
          width: '100%', // Use full width of parent box
          flexWrap: 'wrap',
          justifyContent: 'center' // Center the steps within the stepper
        }
      }}>
        <StageStepper 
          stageDetails={stageDetails} 
          currentStage={currentStage} 
          onStageClick={handleStageClick}
        />
      </Box>
      
      <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
        <Grid item xs={12} md={8} sx={{ width: { md: '66.666%' } }}>
          <MermaidDiagram 
            isVisible={isVisible}
            currentStage={currentStage}
            selectedStep={selectedStep}
            completedSteps={completedSteps}
            onNodeClick={handleStageClick}
          />
        </Grid>
        
        <Grid item xs={12} md={4} sx={{ width: { md: '33.333%' } }}>
          <StageDetails 
            stageDetails={stageDetails[currentStage as keyof typeof stageDetails]} 
            currentStage={currentStage}
            selectedStepObject={selectedStep as ProcessStep}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ProcessFlow;